'use client';

/**
 * DemoAskInputCard — mode8's agentic Ask Klyde input surface.
 *
 * Chat-shaped input. User types a question, hits Enter (or the send
 * button); the request goes to the `ki_ask` BFF strategy via the
 * unified data gateway (`/ui/data`). The response — including
 * `simulation_id`, resolved `project`, and the agent's intent — lands
 * in `useDemoAskStore`. The sibling renderer slot reads from the same
 * store and decides what to mount.
 *
 * Substrate-only — no static content. Parallel to Reception's
 * `WebQAInputCard` by design: same dispatched-card-slot pattern,
 * different post target (`ki_ask` vs `docs_ask`), different store
 * (`useDemoAskStore` vs `useDocsStore`).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Loader2, FlaskConical } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import type { AskResponse, TestRunResponse } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION } from './web-qa-chrome';


interface AskRequestParams {
  query: string;
  context_hints?: Record<string, unknown>;
  // Multi-turn — set to the previous turn's simulation_id to append
  // this turn to the same parent :Conversation. AskService routes
  // through record_turn() (vs execute()) when present; intent
  // extraction gets the previous turn's narrative + entity threaded
  // into the prompt so follow-up pronouns resolve. Null on turn 1.
  conversation_id?: string | null;
}


interface TestRunnerParams {
  case_id: string;
}


/** Chip summary read from the `test_cases` BFF — same shape as
 *  TestCaseSummary on the wire. Fields are the canonical case identity
 *  plus the demo metadata that controls chip rendering. */
interface DemoableCase {
  case_id: string;
  name: string;
  demo_on: string | null;
  demo_tier: string;
}


async function runTestCase(params: TestRunnerParams): Promise<TestRunResponse> {
  const res = await api.getStrategyData<TestRunResponse>('test_runner', params);
  if (!res.success) {
    throw new Error(res.error || 'test_runner failed');
  }
  return res.data;
}


/** Fetch every demoable case for this mode. The chip strip is now
 *  registry-driven: a case with `demo_on="mode8"` set on its
 *  `TestCase` decoration appears here automatically. */
async function fetchDemoableCases(mode: string): Promise<DemoableCase[]> {
  const res = await api.getStrategyData<{ cases: DemoableCase[] }>('test_cases', {});
  if (!res.success) throw new Error(res.error || 'test_cases failed');
  return (res.data.cases || []).filter((c) => c.demo_on === mode);
}


/**
 * Wire envelope for ki_ask — gateway returns `{success, data, renderer_card,
 * error, strategy}`. The renderer_card auto-inject happens at the gateway
 * (see ui_config/data_gateway.py); we hoist it onto the store alongside the
 * AskResponse `data` so the renderer slot can read it without re-fetching.
 */
async function askKiAsk(params: AskRequestParams): Promise<{
  response: AskResponse;
  rendererCard: string | null;
}> {
  const res = await api.getStrategyData<AskResponse>('ki_ask', params);
  if (!res.success) {
    throw new Error(res.error || 'ki_ask failed');
  }
  // ki_ask is a meta-router; the gateway's envelope-level renderer_card
  // is auto-injected from BFF_REGISTRY[ki_ask] which has no renderer_card
  // (ki_ask itself isn't directly rendered). The actual renderer is on
  // AskResponse.renderer_card — set by AskService from the picked
  // capability's @bff_strategy(renderer_card=...) declaration.
  // Prefer the inner field; fall back to the envelope for non-agent
  // strategies that have a static renderer.
  const envelopeRender = (res as unknown as { renderer_card?: string | null }).renderer_card ?? null;
  const rendererCard = res.data.renderer_card ?? envelopeRender;
  return { response: res.data, rendererCard };
}


export function DemoAskInputCard() {
  const T = useCardTypography();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const setResponse = useDemoAskStore((s) => s.setResponse);
  const setTestRun = useDemoAskStore((s) => s.setTestRun);
  const setLoading = useDemoAskStore((s) => s.setLoading);
  const setLastQuery = useDemoAskStore((s) => s.setLastQuery);
  const lastQuery = useDemoAskStore((s) => s.lastQuery);
  // Latest turn's simulation_id is the parent :Conversation. Read off
  // the turns array so the "Start a new conversation" affordance and
  // the conversation_id thread-back both honor the transcript's tail.
  const askSimulationId = useDemoAskStore(
    (s) => s.turns.at(-1)?.response.simulation_id ?? null,
  );
  const testRunSimulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);
  const conversationId = askSimulationId ?? testRunSimulationId;

  // Bridge the agent's resolved entity to the IPE's selection store.
  // Obeya cards (obeya_health, obeya_quality, ...) read project context
  // from useGraphSelectionStore.projectId as their fallback chain —
  // mode8 has no canvas selection, so without this bridge the mounted
  // card sees no projectId and shows "Select a project". When the
  // agent resolves a project entity, push its SID into the same store
  // the cards already consult.
  const setSelectedProject = useGraphSelectionStore((s) => s.selectNode);

  const ask = useMutation<
    { response: AskResponse; rendererCard: string | null },
    Error,
    AskRequestParams
  >({
    mutationFn: askKiAsk,
    onSuccess: ({ response, rendererCard }) => {
      setResponse(response, rendererCard);
      // If the agent landed on a project entity, bridge the SID into
      // the graph-selection store so mounted cards see it as their
      // project context. Non-project entities (rfq/vendor/po) skip —
      // their cards either accept entity_sid directly (future) or land
      // on the agent_graph fallback.
      if (response.entity_sid && response.intent?.entity_type === 'project') {
        setSelectedProject(null, response.entity_sid);
      }
      setQuery('');
    },
    onError: () => setLoading(false),
  });

  const runTest = useMutation<TestRunResponse, Error, TestRunnerParams>({
    mutationFn: runTestCase,
    onSuccess: (testRun) => {
      setTestRun(testRun);
    },
    onError: () => setLoading(false),
  });

  // Chip strip — registry-driven. Cases declare `demo_on="mode8"` on
  // their @register(TestCase(...)) decoration; the strip renders every
  // case whose demo_on matches this mode. No hardcoded list to maintain.
  const { data: demoableCases } = useQuery<DemoableCase[]>({
    queryKey: ['test_cases', 'demoable', 'mode8'],
    queryFn: () => fetchDemoableCases('mode8'),
    staleTime: 5 * 60_000,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = query.trim();
    if (!text) return;
    setLastQuery(text);
    setLoading(true);
    // Thread the previous turn's simulation_id as conversation_id so the
    // backend appends this turn to the same :Conversation. Without this,
    // every follow-up is a fresh conversation — the agent has no prior
    // entity context, no narrative_summary, no prior_entities to resolve
    // descriptive references ("the red RFQ", "what about quality") against.
    // The store's askSimulationId is the parent CV's semantic_id; cleared
    // by the "Start a new conversation" affordance below when the user
    // wants a reset.
    ask.mutate({
      query: text,
      conversation_id: askSimulationId ?? undefined,
    });
  };

  const handleRunTest = (caseId: string) => {
    setLastQuery(`run ${caseId}`);
    setLoading(true);
    runTest.mutate({ case_id: caseId });
  };

  const pendingCaseId = runTest.isPending ? runTest.variables?.case_id ?? null : null;

  const isBusy = ask.isPending || runTest.isPending;

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <h2 className={cn(T.name, 'mb-1')}>Ask Klyde</h2>
        <p className={cn(T.body, 'mb-3')}>
          Ask about a project, RFQ, vendor, or what to do next. The agent picks the
          right capability and runs it.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="How is project HOX going?"
              disabled={isBusy}
              className={cn(
                'w-full pl-5 pr-14 py-3 rounded-full',
                'bg-card border border-border text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40',
                'placeholder:text-muted-foreground transition-all',
                'disabled:opacity-60',
              )}
            />
            <button
              type="submit"
              disabled={!query.trim() || isBusy}
              aria-label="Send question"
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2',
                'flex items-center justify-center w-10 h-10 rounded-full',
                'bg-primary text-primary-foreground',
                'hover:-translate-y-[calc(50%+1px)] hover:shadow-lg hover:shadow-primary/25',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-[-50%] disabled:hover:shadow-none',
                'transition-all',
              )}
            >
              {ask.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>

        {/* Chip strip — registry-driven. Each chip is a TestCase
            decorated with `demo_on="mode8"` on its @register declaration.
            Adding a chip = setting `demo_on=mode8` on a case in
            klyde_testing/cases/*.py. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={cn(T.metadata, 'text-muted-foreground')}>Or run a test:</span>
          {(demoableCases ?? []).map((c) => {
            const isPendingThis = pendingCaseId === c.case_id;
            return (
              <button
                key={c.case_id}
                type="button"
                onClick={() => handleRunTest(c.case_id)}
                disabled={isBusy}
                aria-label={`Run ${c.case_id}`}
                data-testid={`chip-${c.case_id}`}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full',
                  'bg-muted/40 border border-border text-foreground text-sm',
                  'hover:bg-muted/60 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
                title={c.demo_tier ? `${c.demo_tier} canary` : undefined}
              >
                {isPendingThis ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FlaskConical className="w-3 h-3" />
                )}
                <span className="font-mono text-xs">{c.case_id}</span>
                {c.demo_tier && (
                  <span className={cn(T.metadata, 'text-muted-foreground text-[10px] uppercase tracking-wide')}>
                    {c.demo_tier}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {lastQuery && (
          <p className={cn(T.body, 'mt-3 text-sm')}>
            <span className="text-muted-foreground">Asked: </span>
            <span className="text-foreground">{lastQuery}</span>
          </p>
        )}

        {conversationId && (
          <button
            type="button"
            onClick={() => {
              useDemoAskStore.getState().clear();
              setQuery('');
              inputRef.current?.focus();
            }}
            className={cn(T.metadata, 'mt-2 hover:text-foreground transition-colors')}
          >
            Start a new conversation
          </button>
        )}
      </div>
    </section>
  );
}
